import { useEffect, useState } from "react";

/**
 * useTheme — light/dark toggle with localStorage persistence.
 *
 * Applies the theme by toggling the `dark` class on <html>, matching
 * Tailwind's `darkMode: 'class'` setup. Persists the user's choice to
 * `localStorage["theme"]` so it survives reloads.
 *
 * Consumers get back `{ theme, setTheme, toggle }`. Fresh visitors
 * default to light — respecting `prefers-color-scheme` is left as a
 * future enhancement so we don't surprise existing users with a sudden
 * dark app on first open.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem("theme") || "light"; } catch { return "light"; }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem("theme", theme); } catch {}

    // ── Native iOS: sync the status-bar chrome with the theme ──
    // The safe-area strips (notch area + home-indicator area) are
    // NOT part of the WebView — they're painted by iOS itself using
    // the `ios.backgroundColor` from capacitor.config.ts. That config
    // is compile-time, so we also poke the StatusBar plugin at runtime
    // to keep the top strip in sync with light/dark. The bottom strip
    // stays on capacitor.config.ts's baseline color — pick the value
    // that matches your most-common theme.
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return;
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        await StatusBar.setStyle({ style: theme === "dark" ? Style.Dark : Style.Light });
        await StatusBar.setBackgroundColor({ color: theme === "dark" ? "#0F172A" : "#FFFFFF" });
      } catch { /* plugin not installed — no-op on web */ }
    })();
  }, [theme]);

  const setTheme = (next) => setThemeState(next === "dark" ? "dark" : "light");
  const toggle = () => setThemeState((v) => (v === "dark" ? "light" : "dark"));

  return { theme, setTheme, toggle };
}

// Bootstrap: run once at module import so the class flips BEFORE React
// mounts — avoids a light→dark flash on reload for users who chose dark.
try {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem("theme");
    if (saved === "dark") document.documentElement.classList.add("dark");
  }
} catch {}
